/**
 * Net Exposure — Backend-UI parity for the list page
 * (`#!/net-exposure` on backendlight.winner7.co).
 *
 * Live (Lab `net-exposure.js`): `getBooksForBackendPre` then `getBooksForBackend`
 * via `useNetExposureBooks`. Demo / no bet URL: fixture books.
 * Detail Book → workstation; View Total → advance.
 */

import { useMemo, useState } from 'react'
import { Button, Icon, Kpi, PageHead } from '@w7/ui'
import { useNetExposureBooks } from '@/api/hooks/net-exposure/useBooks'
import { useViewerCurrency } from '@/api/hooks/shared/useViewerCurrency'
import { count, money, moneyCompact } from '@/lib/format'
import type { BooksProductScope } from '@/features/book-management/domain'
import { filterEventsByProductScope } from '@/features/net-exposure/productScope'
import { EventBooksTable } from '@/features/net-exposure/EventBooksTable'
import {
  BOOK_TYPES,
  NE_SORTS,
  SPORTS,
  WORSE_THAN_STEPS,
  eventNames,
  filterEvents,
  summariseNetExposure,
  type BookType,
  type NeSort,
  type SportName,
} from './fixture'
import './net-exposure.css'

export function NetExposurePage({
  embedded = false,
  productScope = 'sportsbook',
}: {
  embedded?: boolean
  /** Book Management vs Premium BM — filters artemis books from the shared feed. */
  productScope?: BooksProductScope
} = {}) {
  const [bookType, setBookType] = useState<BookType>('My PT')
  const [sport, setSport] = useState<SportName>('Cricket')
  const [eventName, setEventName] = useState<string>('')
  const [liveOnly, setLiveOnly] = useState(false)
  const [worseThan, setWorseThan] = useState<number | null>(null)
  const [sort, setSort] = useState<NeSort>('worst')

  const { events: rawEvents, isLoading, isError, error, isFetching, refetch } = useNetExposureBooks({
    selectedType: bookType,
    eventType: sport,
    eventname: eventName || 'All',
  })

  const events = useMemo(
    () => filterEventsByProductScope(rawEvents, productScope),
    [rawEvents, productScope],
  )

  const names = useMemo(() => eventNames(events), [events])
  const visible = useMemo(
    () => filterEvents(events, sport, eventName || null, liveOnly, worseThan, sort),
    [events, sport, eventName, liveOnly, worseThan, sort],
  )
  const totals = useMemo(() => summariseNetExposure(visible), [visible])
  const isFiltered =
    sport !== 'All' || Boolean(eventName) || liveOnly || worseThan != null

  const clearFilters = () => {
    setSport('All')
    setEventName('')
    setLiveOnly(false)
    setWorseThan(null)
  }

  const sortLabel = NE_SORTS.find((entry) => entry.id === sort)?.label ?? 'Worst case first'
  const currency = useViewerCurrency()
  const rupees = (n: number) => money(n, { currency })
  const rupeesRound = (n: number) => money(n, { round: true, currency })
  const rupeesCompact = (n: number) =>
    currency === 'INR' ? moneyCompact(n) : money(n, { round: true, currency })

  return (
    <div className="w7-ne">
      {embedded ? null : (
        <PageHead
          title={
            <span className="w7-ne-title">
              <Icon name="net-exposure" size={22} className="w7-ne-title-mark" />
              Net Exposure
            </span>
          }
          subtitle="Open event books beneath you — worst case first, same maths as Exposure."
          actions={
            <Button variant="secondary" icon="refresh" busy={isFetching} onClick={() => refetch()}>
              Refresh
            </Button>
          }
        />
      )}

      <div className="w7-ne-toolbar">
        <div className="w7-ne-bar">
          <div
            className="w7-ne-seg"
            role="group"
            aria-label="Book type"
            data-active={bookType === 'Total Book' ? 'total' : 'pt'}
          >
            <span className="w7-ne-seg-thumb" aria-hidden="true" />
            {BOOK_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                className="w7-ne-seg-btn"
                aria-pressed={bookType === type}
                onClick={() => setBookType(type)}
              >
                {type}
              </button>
            ))}
          </div>

          <div className="w7-ne-chips" role="group" aria-label="Sport">
            {SPORTS.map((item) => (
              <button
                key={item.value}
                type="button"
                className="w7-ne-chip"
                aria-pressed={sport === item.value}
                onClick={() => {
                  setSport(item.value)
                  setEventName('')
                }}
              >
                {item.name}
              </button>
            ))}
            <button
              type="button"
              className="w7-ne-chip"
              aria-pressed={liveOnly}
              onClick={() => {
                const next = !liveOnly
                if (next && eventName) {
                  const selected = events.find((e) => e._id === eventName)
                  if (selected && !selected.inPlay) setEventName('')
                }
                setLiveOnly(next)
              }}
            >
              In play only
            </button>
          </div>

          <span className="w7-spacer" />

          {isFiltered ? (
            <Button
              variant="ghost"
              icon="close"
              onClick={clearFilters}
              title="Clears sport, event, live and worse-than. Book and sort stay."
            >
              Clear
            </Button>
          ) : null}

          {/* Embedded there is no page head to hang Refresh off, and a row of its
              own for one button is a row of nothing. */}
          {embedded ? (
            <Button variant="secondary" icon="refresh" busy={isFetching} onClick={() => refetch()}>
              Refresh
            </Button>
          ) : null}
        </div>

        <div className="w7-ne-bar w7-ne-bar-controls">
          <select
            className="w7-ne-select w7-ne-select-event"
            value={eventName}
            onChange={(event) => setEventName(event.target.value)}
            aria-label="Select or search event"
          >
            <option value="">All events</option>
            {names
              .filter((name) => {
                const match = events.find((e) => e._id === name)
                if (!match) return false
                if (sport !== 'All' && match.sport !== sport) return false
                if (liveOnly && !match.inPlay) return false
                return true
              })
              .map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
          </select>

          <select
            className="w7-ne-select"
            value={worseThan === null ? '' : String(worseThan)}
            onChange={(event) =>
              setWorseThan(event.target.value ? Number(event.target.value) : null)
            }
            aria-label="Show only positions worse than"
          >
            <option value="">Any worst case</option>
            {WORSE_THAN_STEPS.map((step) => (
              <option key={step} value={String(step)}>
                Worse than {rupeesRound(step)}
              </option>
            ))}
          </select>

          <select
            className="w7-ne-select"
            value={sort}
            onChange={(event) => setSort(event.target.value as NeSort)}
            aria-label="Sort events by"
          >
            {NE_SORTS.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.label}
              </option>
            ))}
          </select>
        </div>

        <p className="w7-ne-filter-meta" aria-live="polite">
          {count(totals.events)} {totals.events === 1 ? 'event' : 'events'} ·{' '}
          {count(totals.markets)} {totals.markets === 1 ? 'market' : 'markets'} ·{' '}
          {count(totals.fancyLines)} fancy · {sortLabel}
          {worseThan != null ? ` · worse than ${rupeesRound(worseThan)}` : ''}
          {liveOnly ? ' · in play' : ''}
          {` · ${bookType}`}
        </p>
      </div>

      <div className="w7-grid w7-grid-5 w7-ne-kpis">
        <Kpi
          label="Worst case"
          value={rupeesCompact(totals.worstCase)}
          exact={rupees(totals.worstCase)}
          tone={totals.worstCase < 0 ? 'loss' : 'neutral'}
          accent
          meta={`Across ${count(totals.events)} ${totals.events === 1 ? 'event' : 'events'}`}
        />
        <Kpi
          label="Best case"
          value={rupeesCompact(totals.bestCase)}
          exact={rupees(totals.bestCase)}
          tone={totals.bestCase > 0 ? 'profit' : 'neutral'}
          meta="If every market lands the book's way"
        />
        <Kpi
          label="In play"
          value={rupeesCompact(totals.liveWorst)}
          exact={rupees(totals.liveWorst)}
          tone={totals.liveWorst < 0 ? 'loss' : 'neutral'}
          meta={`${count(totals.liveEvents)} of ${count(totals.events)} events are live`}
          onClick={() => setLiveOnly((value) => !value)}
        />
        <Kpi
          label="Markets"
          value={count(totals.markets)}
          meta={`In ${count(totals.events)} ${totals.events === 1 ? 'event' : 'events'}`}
        />
        <Kpi
          label="Fancy lines"
          value={count(totals.fancyLines)}
          meta="Session and fancy books in view"
        />
      </div>

      <div className="w7-ne-kpi-note">
        <p className="w7-card-sub">
          Worst case adds up the losing outcome of every market as though they all settled against
          the book at the same moment. Inside a single market the 1, X and 2 cells are alternatives —
          the row&rsquo;s worst is the smallest of them, never their sum.
        </p>
        <p className="w7-ne-basis-note" data-book-type={bookType}>
          Showing <strong>{bookType}</strong>
          {sport !== 'All' ? (
            <>
              {' '}
              · <strong>{sport}</strong>
            </>
          ) : null}
          {liveOnly ? (
            <>
              {' '}
              · <strong>In play only</strong>
            </>
          ) : null}
          {worseThan != null ? (
            <>
              {' '}
              · worse than <strong>{rupeesRound(worseThan)}</strong>
            </>
          ) : null}
        </p>
        {isFiltered ? (
          <p className="w7-card-sub w7-dimmer">
            These figures describe the {count(totals.events)}{' '}
            {totals.events === 1 ? 'event' : 'events'} in view, not your whole book.
          </p>
        ) : null}
      </div>

      {isLoading ? (
        <div className="w7-ne-empty">Loading open books…</div>
      ) : isError ? (
        <div className="w7-ne-empty">
          Could not load books
          {error instanceof Error && error.message ? `: ${error.message}` : ''}
        </div>
      ) : visible.length === 0 ? (
        <div className="w7-ne-empty">
          {isFiltered ? 'No events match these filters' : 'No Data Found'}
        </div>
      ) : (
        <div className="w7-ne-events">
          {visible.map((event) => (
            <EventBooksTable key={event._id} event={event} book={bookType} />
          ))}
        </div>
      )}
    </div>
  )
}
